// frontend/src/pages/admin/Orders.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiFileText,
  FiSearch,
  FiFilter,
  FiEye,
  FiClock,
  FiCheckCircle,
  FiTruck,
  FiXCircle,
  FiPackage,
  FiAlertCircle
} from 'react-icons/fi';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');

  useEffect(() => {
    fetchOrders();
  }, [pagination.page, pagination.limit, searchQuery, statusFilter, paymentStatusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/orders', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery,
          status: statusFilter,
          payment_status: paymentStatusFilter
        }
      });

      if (response.data.success) {
        setOrders(response.data.data.orders);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Fetch orders error:', error);
      toast.error('Захиалга татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handlePaymentStatusFilter = (e) => {
    setPaymentStatusFilter(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination({ ...pagination, limit: newLimit, page: 1 });
  };

  const goToPage = (page) => {
    setPagination({ ...pagination, page });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: FiClock, label: 'Хүлээгдэж буй' },
      accepted: { bg: 'bg-blue-100', text: 'text-blue-800', icon: FiCheckCircle, label: 'Баталгаажсан' },
      prepared: { bg: 'bg-purple-100', text: 'text-purple-800', icon: FiPackage, label: 'Бэлтгэгдсэн' },
      dispatched: { bg: 'bg-indigo-100', text: 'text-indigo-800', icon: FiTruck, label: 'Хүргэлтэнд гарсан' },
      delivered: { bg: 'bg-green-100', text: 'text-green-800', icon: FiCheckCircle, label: 'Хүргэгдсэн' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: FiXCircle, label: 'Цуцлагдсан' }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon size={12} />
        {badge.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (order) => {
    // Хэрэв pending_verification бол
    if (order.payment_method === 'bank_transfer' && 
        order.payment_status === 'unpaid' &&
        order.transaction_status === 'pending_verification') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Баталгаажуулалт хүлээж байна
        </span>
      );
    }

    const badges = {
      unpaid: { bg: 'bg-red-100', text: 'text-red-800', label: 'Төлөөгүй' },
      partial: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Хэсэгчлэн' },
      paid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Төлсөн' },
      refunded: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Буцаасан' }
    };

    const badge = badges[order.payment_status] || badges.unpaid;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      cash: 'Бэлэн',
      card: 'Карт',
      bank_transfer: 'Шилжүүлэг',
      credit: 'Зээл'
    };
    return methods[method] || method;
  };

  return (
    <div className="orders-page">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <FiFileText size={28} className="text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Захиалгууд</h1>
            <p className="text-gray-600 mt-1">Захиалгын удирдлага</p>
          </div>
        </div>

        {/* Filters */}
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                  placeholder="Захиалгын дугаар эсвэл харилцагчийн нэр..."
                  className="input pl-10"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={handleStatusFilter}
                className="input"
              >
                <option value="">Бүх статус</option>
                <option value="pending">Хүлээгдэж буй</option>
                <option value="accepted">Баталгаажсан</option>
                <option value="prepared">Бэлтгэгдсэн</option>
                <option value="dispatched">Хүргэлтэнд гарсан</option>
                <option value="delivered">Хүргэгдсэн</option>
                <option value="cancelled">Цуцлагдсан</option>
              </select>
            </div>

            {/* Payment Status Filter */}
            <div>
              <select
                value={paymentStatusFilter}
                onChange={handlePaymentStatusFilter}
                className="input"
              >
                <option value="">Төлбөрийн статус</option>
                <option value="unpaid">Төлөөгүй</option>
                <option value="partial">Хэсэгчлэн</option>
                <option value="paid">Төлсөн</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card bg-yellow-50 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Хүлээгдэж буй</p>
              <p className="text-2xl font-bold text-yellow-600">
                {orders.filter(o => o.status === 'pending').length}
              </p>
            </div>
            <FiClock className="text-yellow-600" size={32} />
          </div>
        </div>

        <div className="card bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Баталгаажуулах</p>
              <p className="text-2xl font-bold text-blue-600">
                {orders.filter(o => o.transaction_status === 'pending_verification').length}
              </p>
            </div>
            <FiAlertCircle className="text-blue-600" size={32} />
          </div>
        </div>

        <div className="card bg-purple-50 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Бэлтгэгдсэн</p>
              <p className="text-2xl font-bold text-purple-600">
                {orders.filter(o => o.status === 'prepared').length}
              </p>
            </div>
            <FiPackage className="text-purple-600" size={32} />
          </div>
        </div>

        <div className="card bg-green-50 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Хүргэгдсэн</p>
              <p className="text-2xl font-bold text-green-600">
                {orders.filter(o => o.status === 'delivered').length}
              </p>
            </div>
            <FiCheckCircle className="text-green-600" size={32} />
          </div>
        </div>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="spinner-lg"></div>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-th">Захиалга</th>
                <th className="table-th">Харилцагч</th>
                <th className="table-th">Огноо</th>
                <th className="table-th text-right">Дүн</th>
                <th className="table-th">Төлбөр</th>
                <th className="table-th">Төлбөрийн төлөв</th>
                <th className="table-th">Статус</th>
                <th className="table-th text-right">Үйлдэл</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-gray-500">
                    <FiFileText size={48} className="mx-auto mb-3 opacity-50" />
                    <p>Захиалга олдсонгүй</p>
                  </td>
                </tr>
              ) : (
                orders.map(order => (
                  <tr key={order.id} className="table-row">
                    <td className="table-td">
                      <p className="font-semibold text-gray-900">#{order.order_number}</p>
                      <p className="text-xs text-gray-500">{order.total_quantity} бараа</p>
                    </td>
                    <td className="table-td">
                      <p className="font-medium text-gray-900">{order.customer_name}</p>
                      <p className="text-xs text-gray-500">{order.customer_phone}</p>
                    </td>
                    <td className="table-td">
                      <p className="text-sm text-gray-900">
                        {new Date(order.created_at).toLocaleDateString('mn-MN')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(order.created_at).toLocaleTimeString('mn-MN', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </td>
                    <td className="table-td text-right">
                      <p className="font-bold text-gray-900">
                        ₮{order.total_amount.toLocaleString()}
                      </p>
                      {order.discount_rate > 0 && (
                        <p className="text-xs text-green-600">
                          -{order.discount_rate}%
                        </p>
                      )}
                    </td>
                    <td className="table-td">
                      <span className="badge badge-gray text-xs">
                        {getPaymentMethodLabel(order.payment_method)}
                      </span>
                    </td>
                    <td className="table-td">
                      {getPaymentStatusBadge(order)}
                    </td>
                    <td className="table-td">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="table-td text-right">
                      <Link
                        to={`/admin/orders/${order.id}`}
                        className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                      >
                        <FiEye size={18} />
                        <span>Харах</span>
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination-container">
          <p className="pagination-info">
            Нийт {pagination.total} захиалга
          </p>
          <div className="pagination-controls">
            <button
              onClick={() => goToPage(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="pagination-button"
            >
              Өмнөх
            </button>
            <span className="pagination-page-info">
              {pagination.page} / {pagination.pages}
            </span>
            <button
              onClick={() => goToPage(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="pagination-button"
            >
              Дараах
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;